import { FormControl, FormLabel } from "@chakra-ui/react";
import { Messages } from "../../../core/api";
import { useGlobalWorkflowRunner } from "../../../shared/hooks/useGlobalWorkflowRunner";
import { InferWorkflowEntityFieldType, WorkflowEntityFieldLiteral } from "../workflow.types";
import { RequiredAst } from "./RequiredAst";
import WorkflowEntityFormControl, { WorkflowEntitySelect } from "./WorkflowEntityFormControl";

type EntityInput = Messages["WorkflowEntityField"] & {
  entity: keyof WorkflowEntityFieldLiteral;
};

type ArrayInputProps<TInput extends EntityInput> = {
  multiple: true;
  value: InferWorkflowEntityFieldType<TInput>[] | null;
  onChange: (value: InferWorkflowEntityFieldType<TInput>[] | null) => void;
};

type NonArrayInputProps<TInput extends EntityInput> = {
  multiple: false;
  value: InferWorkflowEntityFieldType<TInput> | null;
  onChange: (value: InferWorkflowEntityFieldType<TInput> | null) => void;
};

type Props<TInput extends EntityInput> = {
  input: TInput;
  isLoading?: boolean;
  isDisabled?: boolean;
  errors?: string[];
  isRequired?: boolean;
  multiple: boolean;
  label: string;
  renderUnselected?: string;
  defaultFilters?: Record<PropertyKey, unknown>;
  hardFilters?: Record<PropertyKey, unknown>;
  renderHint?: boolean;
} & (ArrayInputProps<TInput> | NonArrayInputProps<TInput>);

export default function EntityFormControl<TInput extends EntityInput>(
  props: Props<TInput> & { showLabel?: boolean }
) {
  return (
    <FormControl isInvalid={(props.errors ?? []).length > 0}>
      {props.showLabel !== false && (
        <FormLabel>
          {props.label}
          {props.isRequired && <RequiredAst />}:
        </FormLabel>
      )}
      <EntitySelect renderHint={true} {...props} />
    </FormControl>
  );
}

export function EntitySelect<TInput extends EntityInput>(props: Props<TInput>) {
  const { getHintOfField } = useGlobalWorkflowRunner();

  const handleChange: React.ComponentProps<typeof WorkflowEntityFormControl>["onChange"] = (
    value
  ) => {
    const definedValues = value.filter(Boolean) as InferWorkflowEntityFieldType<TInput>[];

    return props.multiple
      ? props.onChange(definedValues.length > 0 ? definedValues : null)
      : props.onChange(definedValues.length > 0 ? definedValues[0] : null);
  };

  const handleChangeField: React.ComponentProps<
    typeof WorkflowEntityFormControl
  >["onChangeField"] = (e, index) => {
    const value = e.target.value;
    const arrValue = Array.isArray(props.value) ? props.value : [props.value];

    return handleChange(arrValue.map((x, i) => (i === index ? (value === "" ? null : value) : x)));
  };

  return (
    <WorkflowEntitySelect
      defaultFilters={props.defaultFilters}
      errors={props.errors ?? []}
      hardFilters={props.hardFilters}
      hint={getHintOfField(props.input)}
      input={{ ...props.input, array: props.multiple, name: props.label }}
      isDisabled={props.isDisabled}
      isLoading={props.isLoading}
      isRequired={props.isRequired ?? false}
      renderHint={props.renderHint ?? false}
      renderUnselected={props.renderUnselected}
      value={Array.isArray(props.value) ? props.value : [props.value]}
      onChange={handleChange}
      onChangeField={handleChangeField}
    />
  );
}
